import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ReviewEntry, ReviewsInsight } from 'apis/merchant-gateway/insights/reviews/types';
import { CreditAcquisition } from 'apis/restaurant-api/merchant-admin/credits/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { useRetailContext } from 'common/hooks';
import { InfiniteTable } from 'modules/experience-core';
import { InsightResponsePaginated } from 'modules/insights-v2/types';

import CompensationOffer from '../CompensationOffers/CompensationOffer';
import { useIsOffersEnabled, useSentOffers } from '../CompensationOffers/useCompensationOffers';
import { RatingSmiley } from '../Smiley';
import ThumbsIcon from '../ThumbsIcon';

export enum VenueReviewsTableColumnIDs {
  RATING = 'RATING',
  PURCHASE_DATE = 'PURCHASE_DATE',
  ATTRIBUTIONS = 'ATTRIBUTIONS',
  COMMENTS = 'COMMENTS',
  OFFERS = 'OFFERS',
}
export type VenueReviewsTableSortableColumnIDs =
  | VenueReviewsTableColumnIDs.RATING
  | VenueReviewsTableColumnIDs.PURCHASE_DATE;

type ColumnDef = {
  width: number;
  id: VenueReviewsTableColumnIDs;
  label: string;
  render: (record: ReviewTableItem) => React.ReactNode;
};
export type ReviewTableItem = ReviewEntry & { id: string };
export type ReviewsTableProps = {
  className?: string;
  fetchingNextPage: boolean;
  fetchingPreviousPage: boolean;
  onFetchNextPage?: () => void;
  onFetchPreviousPage?: () => void;
  items: ReviewTableItem[];
  sort?: InfiniteTable.SortState<VenueReviewsTableSortableColumnIDs>;
  columns: ColumnDef[];
  venue: Venue;
  sentOffers?: CreditAcquisition[];
  onSendOffer?: () => void;
};

const TrimmedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const SmileyTableWrapper = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  margin-inline-end: 8px;
`;
const AttributionRoot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;
const ThumbsIconWrapper = styled.div`
  width: 16px;
  height: 16px;
`;

const getColumnWidth = (columns: { widthRatio?: number }[], column: { widthRatio?: number }) => {
  const widthRatiosSum = columns.reduce((acc, { widthRatio }) => acc + (widthRatio ?? 1), 0);
  const percentsPerRatio = 100 / widthRatiosSum;

  const columnWidthRatio = column.widthRatio ?? 1;
  const columnWidthPercentage = percentsPerRatio * columnWidthRatio;
  return columnWidthPercentage;
};

const getReviewAttributionTranslations = (isRetailContext: boolean): Record<string, string> => ({
  appearance: 'purchases.attributions.appearance',
  atmosphere: 'purchases.attributions.atmosphere',
  'cold food': 'purchases.attributions.coldFood',
  comment: 'purchases.attributions.comment',
  courier: 'purchases.attributions.courier',
  'delivery instructions': 'purchases.attributions.deliveryInstructions',
  'delivery time': 'purchases.attributions.deliveryTime',
  estimate: 'purchases.attributions.estimate',
  experience: 'purchases.attributions.experience',
  flavour: 'purchases.attributions.flavour',
  food: 'purchases.attributions.food',
  'had to queue': 'purchases.attributions.hadToQueue',
  'missing item': 'purchases.attributions.missingItem',
  packaging: 'purchases.attributions.packaging',
  'portion size': 'purchases.attributions.portionSize',
  'product damaged': 'purchases.attributions.productDamaged',
  quality: 'purchases.attributions.quality',
  selection: 'purchases.attributions.selection',
  staff: 'purchases.attributions.staff',
  texture: 'purchases.attributions.texture',
  'value for money': 'purchases.attributions.valueForMoney',
  venue: isRetailContext ? 'purchases.attributions.venue-retail' : 'purchases.attributions.venue',
});

const getColumnDefs = ({
  handleSort,
  intl,
  venue,
  isRetailContext,
  isOffersEnabled,
  sentOffers,
  onSendOffer,
}: {
  handleSort?: (sort: InfiniteTable.SortState<VenueReviewsTableSortableColumnIDs>) => void;
  intl: IntlShape;
  venue: Venue;
  isRetailContext: boolean;
  isOffersEnabled: boolean;
  sentOffers?: CreditAcquisition[];
  onSendOffer: () => void;
}): ColumnDef[] => {
  const reviewAttributionTranslations = getReviewAttributionTranslations(isRetailContext);

  const columns = [
    {
      id: VenueReviewsTableColumnIDs.RATING,
      label: intl.formatMessage({ id: 'insights.ratings-and-reviews.reviews-table-column.rating' }),
      render: (record: ReviewTableItem) => (
        <>
          <SmileyTableWrapper>
            <RatingSmiley rating={record.rating} variant="colored" />
          </SmileyTableWrapper>
          <TrimmedText>
            <FormattedMessage id={`insights.ratings-and-reviews.rating-level-name.${record.rating}`} />
          </TrimmedText>
        </>
      ),
      enableSort: true,
      handleSort: (sortOrder: InfiniteTable.SortOrder) =>
        handleSort?.({ sortBy: VenueReviewsTableColumnIDs.RATING, sortOrder }),
      widthRatio: 0.6,
    },
    {
      id: VenueReviewsTableColumnIDs.PURCHASE_DATE,
      label: intl.formatMessage({ id: 'insights.ratings-and-reviews.reviews-table-column.order-created-date' }),
      render: (record: ReviewTableItem) => (
        <TrimmedText>
          {DateTime.fromISO(record.purchaseLocalTime, { zone: venue.timezone }).toLocaleString(
            DateTime.DATETIME_SHORT,
            {
              locale: intl.locale,
            },
          )}
        </TrimmedText>
      ),

      enableSort: true,
      handleSort: (sortOrder: InfiniteTable.SortOrder) =>
        handleSort?.({ sortBy: VenueReviewsTableColumnIDs.PURCHASE_DATE, sortOrder }),
      widthRatio: 0.8,
    },
    {
      id: VenueReviewsTableColumnIDs.ATTRIBUTIONS,
      label: intl.formatMessage({ id: 'insights.ratings-and-reviews.reviews-table-column.feedback' }),
      render: (record: ReviewTableItem) => {
        const attributions = record.attributions
          .map(attribution => attribution.replace('_', ' ').toLowerCase())
          .filter(attribution => !!reviewAttributionTranslations[attribution]);
        return (
          <AttributionRoot>
            {attributions.length === 0 ? (
              '-'
            ) : (
              <>
                <ThumbsIconWrapper>
                  <ThumbsIcon positive={record.rating === 5} />
                </ThumbsIconWrapper>
                <TrimmedText>
                  {intl.formatList(
                    attributions.map(attribution =>
                      intl.formatMessage({
                        id: reviewAttributionTranslations[attribution],
                        defaultMessage: attribution,
                      }),
                    ),
                    { style: 'short' },
                  )}
                </TrimmedText>
              </>
            )}
          </AttributionRoot>
        );
      },
      widthRatio: 0.9,
    },
    {
      id: VenueReviewsTableColumnIDs.COMMENTS,
      label: intl.formatMessage({ id: 'insights.ratings-and-reviews.reviews-table-column.comments' }),
      render: (record: ReviewTableItem) => <TrimmedText>{record.comment}</TrimmedText>,
    },
    ...(isOffersEnabled
      ? [
          {
            id: VenueReviewsTableColumnIDs.OFFERS,
            label: intl.formatMessage({ id: 'insights.ratings-and-reviews.offers.offers-column-title' }),
            widthRatio: 0.5,
            render: (record: ReviewTableItem) =>
              // Show the offer content only if the request for sent offers succeedes
              !!sentOffers && (
                <CompensationOffer
                  venueId={venue.id}
                  currency={venue.currency}
                  timezone={venue.timezone}
                  purchaseId={record.purchaseId}
                  rating={record.rating}
                  sentOffer={sentOffers.find(offer => offer.purchase_id.$oid === record.purchaseId)}
                  onSendOffer={onSendOffer}
                />
              ),
          },
        ]
      : []),
  ];

  return columns.map(column => ({
    ...column,
    width: getColumnWidth(columns, column),
  }));
};

export const useVenueReviewsTableProps = ({
  venue,
  sort,
  setSort: handleSort,
  reviewsInsights,
}: {
  venue: Venue;
  sort: InfiniteTable.SortState<VenueReviewsTableSortableColumnIDs>;
  setSort: (sort: InfiniteTable.SortState<VenueReviewsTableSortableColumnIDs>) => void;
  reviewsInsights: InsightResponsePaginated<ReviewsInsight>;
}) => {
  const {
    data: performanceByVenueData,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = reviewsInsights;

  const intl = useIntl();
  const isRetailContext = useRetailContext();

  const items: ReviewTableItem[] = useMemo(
    () =>
      performanceByVenueData?.pages.reduce(
        (pagesRes, page): ReviewTableItem[] =>
          page.data.reviews.reduce((res, item) => [...res, { ...item, id: item.purchaseId }], pagesRes) ?? pagesRes,
        [],
      ) ?? [],
    [performanceByVenueData?.pages],
  );

  const purchaseIds = useMemo(() => items.map(item => item.purchaseId), [items]);
  const { isOffersEnabled } = useIsOffersEnabled(venue);
  const { data: sentOffers, refetch: refetchSentOffers } = useSentOffers({
    venueId: venue.id,
    purchaseIds,
    isEnabled: isOffersEnabled,
  });

  const columns = useMemo(
    () =>
      getColumnDefs({
        handleSort,
        intl,
        venue,
        isRetailContext,
        isOffersEnabled,
        sentOffers,
        onSendOffer: refetchSentOffers,
      }),
    [handleSort, intl, isRetailContext, venue, isOffersEnabled, sentOffers, refetchSentOffers],
  );

  const tableProps = {
    fetchingNextPage: isFetchingNextPage,
    fetchingPreviousPage: isFetchingPreviousPage,
    items,
    sort,
    onFetchNextPage: hasNextPage ? fetchNextPage : undefined,
    onFetchPreviousPage: hasPreviousPage ? fetchPreviousPage : undefined,
    columns,
    sentOffers: isOffersEnabled ? sentOffers : undefined,
    onSendOffer: isOffersEnabled ? refetchSentOffers : undefined,
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => tableProps, [...Object.values(tableProps)]);
};
